import {
  DropdownButtonType,
  List,
  ListItem,
  RadioButtonWithLabelGroupWidget,
  SingleSelectDropdown,
  TabStyles,
} from '@tableau/widgets';
import * as React from 'react';
import { connect } from 'react-redux';
import { Messages } from '../../codegen/Localize';
import { AskDataMode } from '../../settings/types/AskDataMode';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IDataSourceInfo, NlpSetting } from '../../ts/models/IDataSourceInfo';
import { updateNlpSetting, UpdateNlpSettingAction } from '../actions/UpdateNlpSettingAction';
import {
  CompositeSessionInfoSelectors,
  selectSessionSite,
  selectSessionUser,
} from '../selectors/SessionInfoSelectors';
import { selectSpaceInfoItem } from '../selectors/SpaceInfoSelectors';
import { AppState } from '../state/AppState';

type StateProps = {
  readonly askDataMode: AskDataMode;
  readonly canAuthorOnWeb: boolean;
  readonly nlpSetting: NlpSetting;
  readonly isAdminOrOwner: boolean;
};

type DispatchProps = {
  readonly onUpdateNlpSetting: (nlpOption: NlpSetting) => UpdateNlpSettingAction;
};

export type AskDataSettingsProps = StateProps & DispatchProps;

type AskDataSettingsState =  {
  editMode: boolean;
  nlpSetting: NlpSetting;
  radioButtonIndex: number;
};

export namespace AskDataTestIds {
  export const messageId = 'ask-data-settings-message';
  export const editButton = 'ask-data-settings-edit-button';
  export const saveButton = 'ask-data-settings-save-button';
  export const siteDefaultEnabledMessage = 'ask-data-settings-site-default-enabled-message';
  export const datasourceIndexing = 'ask-data-settings-indexing';
}

const getButtonList = () => [
  {
    label: Messages.dataSourceNlpSetting_indexing_option_automatic(),
    testId: 'automatic',
    value: NlpSetting.refresh24Hrs,
  },
  {
    label: Messages.dataSourceNlpSetting_indexing_option_manual(),
    testId: 'manual',
    value: NlpSetting.enabled,
  },
  {
    label: Messages.dataSourceNlpSetting_indexing_option_disabled(),
    testId: 'disabled',
    value: NlpSetting.disableIndex,
  },
];

export class AskDataSettingsComponent extends React.Component<AskDataSettingsProps, AskDataSettingsState> {
  public constructor(props: AskDataSettingsProps) {
    super(props);
    this.state = this.getInitialState(props);
    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleCancelClick = this.handleCancelClick.bind(this);
    this.handleSaveClick = this.handleSaveClick.bind(this);
    this.handleSettingChange = this.handleSettingChange.bind(this);
    this.handleChangeIndex = this.handleChangeIndex.bind(this);
  }

  private getInitialState(props: AskDataSettingsProps): AskDataSettingsState {
    const buttonList = getButtonList();
    let radioButtonIndex = buttonList.findIndex(item => item.value === NlpSetting.enabled);
    let nlpSetting = props.nlpSetting;
    if (nlpSetting === NlpSetting.refresh24Hrs || nlpSetting === NlpSetting.disableIndex) {
      nlpSetting = NlpSetting.enabled;
      radioButtonIndex = buttonList.findIndex(item => item.value === props.nlpSetting);
    }
    return {
      editMode: false,
      nlpSetting,
      radioButtonIndex,
    };
  }

  private handleEditClick() {
    const initialState = this.getInitialState(this.props);
    this.setState({
      ...initialState,
      editMode: true,
    });
  }

  private handleCancelClick() {
    this.setState({ editMode: false });
  }

  private handleSaveClick() {
    let nlpSetting = this.state.nlpSetting;
    if (nlpSetting === NlpSetting.enabled) {
      const buttonList = getButtonList();
      nlpSetting = buttonList[this.state.radioButtonIndex].value;
    }
    this.props.onUpdateNlpSetting(nlpSetting);
    // Temporary solution to exit edit mode, until Stylish team implements close dialog
    // TFS 1157066 is created to clean this up
    setTimeout(
      () => this.setState({ editMode: false }),
      500,
    );
  }

  private handleSettingChange(value: string) {
    this.setState({ nlpSetting: value as NlpSetting });
  }

  private handleChangeIndex(value: string, radioButtonIndex: number) {
    this.setState({ radioButtonIndex });
  }

  private getMessage(webAuthoringViewerEnablementPermission: boolean): string {
    // remove this block, when nlpviewerEnablementEnabled is removed
    if (!webAuthoringViewerEnablementPermission) {
      return Messages.dataSourceNlpSetting_error_state_NonAuthoring();
    }
    switch (this.props.nlpSetting) {
      case NlpSetting.disabled:
        return Messages.dataSourceNlpSetting_disabled();
      case NlpSetting.siteDefault:
        if (this.props.askDataMode === AskDataMode.ENABLED_BY_DEFAULT) {
          return Messages.dataSourceNlpSetting_siteDefault_enabled();
        }
        if (this.props.askDataMode === AskDataMode.DISABLED_BY_DEFAULT) {
          return Messages.dataSourceNlpSetting_siteDefault_disabled();
        }
      case NlpSetting.enabled:
        return Messages.dataSourceNlpSetting_enabled();
      case NlpSetting.disableIndex:
        return Messages.dataSourceNlpSetting_indexDisabled();
      case NlpSetting.refresh24Hrs:
        return Messages.dataSourceNlpSetting_timedRefresh();
      default:
        return '';
    }
  }

  render() {
    if (this.props.askDataMode === AskDataMode.DISABLED_ALWAYS) {
      return null;
    }

    // remove webAuthoringViewerEnablementPermission, when nlpviewerEnablementEnabled is removed
    let webAuthoringViewerEnablementPermission = true;
    if (FeatureFlags.nlpviewerEnablementEnabled === false) {
      webAuthoringViewerEnablementPermission = this.props.canAuthorOnWeb;
    }

    const canEdit = webAuthoringViewerEnablementPermission && this.props.isAdminOrOwner;

    const settingItems: ListItem[] = [
      {
        value: NlpSetting.enabled,
        content: Messages.dataSourceNlpSetting_option_enabled(),
      },
      {
        value: NlpSetting.disabled,
        content: Messages.dataSourceNlpSetting_option_disabled(),
      },
      {
        value: NlpSetting.siteDefault,
        content: this.props.askDataMode === AskDataMode.ENABLED_BY_DEFAULT ?
          Messages.dataSourceNlpSetting_option_siteDefault_enabled() :
          Messages.dataSourceNlpSetting_option_siteDefault_disabled(),
      },
    ];

    return (
      <tr>
        <td className="tb-label">{Messages.nlp()}</td>
        <td className="tb-details-value">
          {!this.state.editMode &&
            <span data-tb-test-id={AskDataTestIds.messageId}>
              {this.getMessage(webAuthoringViewerEnablementPermission)}
            </span>
          }
          {this.state.editMode &&
            <>
              <SingleSelectDropdown
                style={{
                  // The longest item requires 285px width (Spanish language)
                  width: 285,
                  marginBottom: TabStyles.Spacing.DefaultSpace,
                }}
                label=""
                list={<List items={settingItems} />}
                type={DropdownButtonType.Outline}
                onChange={this.handleSettingChange}
                value={this.state.nlpSetting}
              />
              {this.state.nlpSetting === NlpSetting.siteDefault && this.props.askDataMode === AskDataMode.ENABLED_BY_DEFAULT &&
                <div data-tb-test-id={AskDataTestIds.siteDefaultEnabledMessage}>
                  {Messages.dataSourceNlpSetting_option_siteDefault_message()}
                </div>
              }
              {this.state.nlpSetting === NlpSetting.enabled &&
                <div data-tb-test-id={AskDataTestIds.datasourceIndexing}>
                  <div style={{
                    color: TabStyles.Typography.Color.Secondary,
                    fontSize: 15,
                    paddingTop: TabStyles.Spacing.DefaultSpace,
                    paddingBottom: TabStyles.Spacing.DefaultSpace,
                  }}>
                    {Messages.dataSourceNlpSetting_option_Title()}
                  </div>
                  <RadioButtonWithLabelGroupWidget
                    ariaLabel={Messages.dataSourceNlpSetting_option_Title()}
                    buttonList={getButtonList()}
                    checkedIndex={this.state.radioButtonIndex}
                    handleChange={this.handleChangeIndex}
                  />
                </div>
              }
            </>
          }
        </td>
        <td className="tb-details-button-cell">
          {canEdit && !this.state.editMode &&
            <span
              data-tb-test-id={AskDataTestIds.editButton}
              className="tb-outline-button"
              onClick={this.handleEditClick}
            >
              {Messages.dataSourceNlpSetting_editNlpSettingLabel()}
            </span>
          }
          {canEdit && this.state.editMode &&
            <>
              <span
                data-tb-test-id={AskDataTestIds.saveButton}
                className="tb-outline-button tb-go-button"
                onClick={this.handleSaveClick}
              >
                {Messages.save()}
              </span>
              <span
                className="tb-outline-button tb-cancel-button"
                onClick={this.handleCancelClick}
              >
                {Messages.cancel()}
              </span>
            </>
          }
        </td>
      </tr>
    );
  }
}

const getIsAdminOrOwner = (state: AppState) => {
  const dataSourceOwner = (selectSpaceInfoItem(state) as IDataSourceInfo).owner;
  return CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(dataSourceOwner)(state);
};

export const AskDataSettings = connect((state: AppState) => ({
  askDataMode: selectSessionSite(state).askDataMode,
  canAuthorOnWeb: selectSessionUser(state).canAuthorOnWeb,
  nlpSetting: (selectSpaceInfoItem(state) as IDataSourceInfo).nlpSetting,
  isAdminOrOwner: getIsAdminOrOwner(state),
}), {
  onUpdateNlpSetting: updateNlpSetting,
})(AskDataSettingsComponent);
